import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";
import { SAR } from "../components/RiyalIcon";
import {
  ArrowLeft,
  Bike,
  Car,
  ChevronLeft,
  Clock,
  HandPlatter,
  Navigation,
} from "lucide-react";
import ProductSheet from "../components/ProductSheet";
import ProductQuantityControl from "../components/ProductQuantityControl";
import DirectionsAppSheet from "../components/DirectionsAppSheet";
import ClosedBanner, { ClosedPage } from "../components/ClosedBanner";
import { absoluteUrl } from "../components/ImageUpload";
import { readActiveOrder, clearActiveOrder, isFinalStatus } from "../lib/activeOrder";
import useRestaurantBrowserIdentity from "../hooks/useRestaurantBrowserIdentity";

const DIRECTIONS_MARK_URL = (
  "https://customer-assets-4nw71qhi.emergentagent.net/job_carhop-order/artifacts/"
  + "phhl1hwr_IMG_9551.jpeg"
);

export default function RestaurantMenu() {
  const { slug, branchCode } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [activeCat, setActiveCat] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [activeOrder, setActiveOrder] = useState(null);
  const [isCategoryNavSticky, setIsCategoryNavSticky] = useState(false);
  const [directionsSheetOpen, setDirectionsSheetOpen] = useState(false);
  const categoryButtonRefs = useRef(new Map());
  const categoryNavRef = useRef(null);
  const sectionRefs = useRef(new Map());
  const scrollTargetRef = useRef(null);
  const scrollTargetTimerRef = useRef(null);
  const updateActiveSectionRef = useRef(() => {});
  const cart = useCart();
  const setCartRestaurant = cart.setRestaurant;

  useEffect(() => {
    api.get(`/public/restaurants/${slug}`, { params: { branch_code: branchCode } }).then((r) => {
      setData(r.data);
      setCartRestaurant(
        r.data.restaurant.id,
        r.data.restaurant.branch_id || null,
        "STORE",
        r.data.restaurant.branch_code || null,
      );
      if (r.data.categories?.[0]) setActiveCat(r.data.categories[0].id);
    }).catch(() => setNotFound(true));
  }, [branchCode, setCartRestaurant, slug]);

  useRestaurantBrowserIdentity(data?.restaurant);

  // Poll the customer's most recent active order (if any) so a "متابعة الطلب"
  // shortcut stays visible on the menu while the order is still live.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      const stored = readActiveOrder(slug);
      if (!stored) { if (!cancelled) setActiveOrder(null); return; }
      try {
        const { data: o } = await api.get(`/public/orders/${stored.id}`);
        if (cancelled) return;
        if (isFinalStatus(o.status)) {
          clearActiveOrder(slug);
          setActiveOrder(null);
        } else {
          setActiveOrder({ id: o.id, order_number: o.order_number, status: o.status });
        }
      } catch {
        // Order deleted or restaurant unreachable — drop the stale pointer.
        clearActiveOrder(slug);
        if (!cancelled) setActiveOrder(null);
      }
    };
    check();
    const t = setInterval(check, 15000);
    return () => { cancelled = true; clearInterval(t); };
  }, [slug]);

  useEffect(() => {
    if (!data?.categories?.length) return undefined;

    const updateActiveSection = () => {
      const anchor = 92;
      const sectionPositions = data.categories
        .map((category) => {
          const section = sectionRefs.current.get(category.id);
          if (!section) return null;
          const rect = section.getBoundingClientRect();
          return { id: category.id, top: rect.top, bottom: rect.bottom };
        })
        .filter(Boolean);
      const nextSection = sectionPositions
        .filter((section) => section.top <= anchor && section.bottom > anchor)
        .sort((first, second) => second.top - first.top)[0]?.id
        || sectionPositions
          .filter((section) => section.top > anchor)
          .sort((first, second) => first.top - second.top)[0]?.id;

      if (!nextSection) return;
      if (scrollTargetRef.current && scrollTargetRef.current !== nextSection) return;

      scrollTargetRef.current = null;
      setActiveCat((current) => (current === nextSection ? current : nextSection));
    };
    updateActiveSectionRef.current = updateActiveSection;

    const observer = new IntersectionObserver(() => {
      updateActiveSection();
    }, {
      rootMargin: "-76px 0px -42% 0px",
      threshold: [0, 0.1, 0.25, 0.5, 0.75, 1],
    });

    data.categories.forEach((category) => {
      const section = sectionRefs.current.get(category.id);
      if (section) observer.observe(section);
    });

    return () => {
      observer.disconnect();
      updateActiveSectionRef.current = () => {};
    };
  }, [data]);

  useEffect(() => {
    if (!data) return undefined;

    const updateStickyState = () => {
      const navTop = categoryNavRef.current?.getBoundingClientRect().top;
      const nextState = navTop !== undefined && navTop <= 0;

      setIsCategoryNavSticky((current) => (current === nextState ? current : nextState));
    };

    updateStickyState();
    window.addEventListener("scroll", updateStickyState, { passive: true });

    return () => window.removeEventListener("scroll", updateStickyState);
  }, [data]);

  useEffect(() => {
    return () => window.clearTimeout(scrollTargetTimerRef.current);
  }, []);

  useEffect(() => {
    if (!activeCat) return;

    categoryButtonRefs.current.get(activeCat)?.scrollIntoView({
      behavior: "smooth",
      block: "nearest",
      inline: "center",
    });
  }, [activeCat]);

  if (notFound) return <div className="min-h-screen bg-white grid place-items-center text-gray-500">المطعم غير موجود</div>;
  if (!data) return <div className="p-8 text-center text-gray-500">جاري التحميل…</div>;

  const { restaurant, categories, products } = data;
  const branches = data.branches || [];
  const selectedBranchCode = branches.find(
    (branch) => branch.id === cart.branch_id,
  )?.branch_code || restaurant.branch_code;
  const publicBranchBase = (
    `/r/${slug}${selectedBranchCode ? `/${encodeURIComponent(selectedBranchCode)}` : ""}`
  );
  const av = restaurant.availability || { accepting_orders: true };
  const deliveryAvailable = branches.some((branch) => branch.delivery_enabled);
  const orderType = (
    cart.restaurant_id === restaurant.id
    && cart.fulfillment_method === "DELIVERY"
  ) ? "DELIVERY" : "STORE";
  const preparationTime = orderType === "DELIVERY"
    ? restaurant.delivery_prep_time_minutes ?? restaurant.prep_time_minutes ?? 15
    : restaurant.pickup_prep_time_minutes ?? restaurant.prep_time_minutes ?? 15;
  const hasBranchLocation = (
    restaurant.latitude !== null
    && restaurant.latitude !== undefined
    && restaurant.longitude !== null
    && restaurant.longitude !== undefined
    && Number.isFinite(Number(restaurant.latitude))
    && Number.isFinite(Number(restaurant.longitude))
  );
  const branchMapsQuery = hasBranchLocation
    ? `${restaurant.latitude},${restaurant.longitude}`
    : restaurant.address;
  const branchMapsUrl = branchMapsQuery
    ? "https://www.google.com/maps/search/?api=1&query="
      + encodeURIComponent(branchMapsQuery)
    : "";

  const selectOrderType = (nextMethod) => {
    const preferredBranchId = nextMethod === "DELIVERY"
      ? null
      : restaurant.branch_id || cart.branch_id || null;
    const preferredBranchCode = nextMethod === "DELIVERY"
      ? restaurant.branch_code || null
      : branches.find((branch) => branch.id === preferredBranchId)?.branch_code
        || restaurant.branch_code
        || null;
    cart.setOrderContext(
      restaurant.id,
      preferredBranchId,
      nextMethod,
      null,
      preferredBranchCode,
    );
  };

  // Hard block: platform suspended
  if (av.reason_code === "PLATFORM_SUSPENDED") return <ClosedPage availability={av} restaurant={restaurant} />;

  const scrollToCategory = (categoryId) => {
    scrollTargetRef.current = categoryId;
    window.clearTimeout(scrollTargetTimerRef.current);
    scrollTargetTimerRef.current = window.setTimeout(() => {
      const targetSection = sectionRefs.current.get(categoryId);
      const targetRect = targetSection?.getBoundingClientRect();
      scrollTargetRef.current = null;
      if (targetRect && targetRect.bottom > 92 && targetRect.top < window.innerHeight) {
        setActiveCat(categoryId);
      } else {
        updateActiveSectionRef.current();
      }
    }, 700);
    setActiveCat(categoryId);
    sectionRefs.current.get(categoryId)?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  return (
    <div className="min-h-screen bg-white pb-28">
      <div className="relative pt-52">
        <div className="absolute inset-x-0 top-0 h-[300px] bg-orange-200 overflow-hidden">
          {restaurant.cover_url ? <img src={absoluteUrl(restaurant.cover_url)} alt="" className="w-full h-full object-cover" />
           : restaurant.hero_url ? <img src={restaurant.hero_url} alt="" className="w-full h-full object-cover" />
           : null}
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
        </div>
        <div className="max-w-md mx-auto -mt-7 relative px-5">
          <div className="bg-white rounded-3xl card-shadow p-5 border border-orange-100">
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-extrabold" data-testid="restaurant-name">{restaurant.name}</h1>
                <p className="text-sm text-gray-600 mt-1">{restaurant.tagline}</p>
              </div>
              <div className="text-4xl">
                {restaurant.logo_url && restaurant.logo_url.length <= 4 ? restaurant.logo_url :
                  restaurant.logo_url ? <img src={absoluteUrl(restaurant.logo_url)} alt="" className="w-14 h-14 rounded-2xl object-cover" /> : "🍽️"}
              </div>
            </div>
            <div className="mt-3 flex items-center gap-3 text-xs">
              {av.accepting_orders
                ? <span className="inline-flex items-center gap-1 text-green-700 font-bold"><span className="w-2 h-2 rounded-full bg-green-500 inline-block"/> {av.reason}</span>
                : <span className="inline-flex items-center gap-1 text-red-700 font-bold"><span className="w-2 h-2 rounded-full bg-red-500 inline-block"/> {av.reason}</span>}
              {restaurant.car_pickup_enabled && <span className="inline-flex items-center gap-1 text-gray-500"><Car size={12}/> استلام سيارة</span>}
            </div>
            <div className="mt-4 w-full" data-testid="menu-order-type-selector">
              <div
                className="flex w-full rounded-sm border border-gray-200 bg-gray-100 p-1"
                dir="ltr"
              >
                <button
                  type="button"
                  onClick={() => selectOrderType("STORE")}
                  className={[
                    "flex min-h-[35px] flex-1 items-center justify-center rounded-sm px-4 py-1 text-black",
                    orderType === "STORE"
                      ? "border border-white bg-white shadow-sm"
                      : "border border-transparent",
                  ].join(" ")}
                  data-testid="menu-order-type-store"
                  dir="rtl"
                >
                  <span className="flex items-center gap-1.5 text-sm font-bold">
                    <HandPlatter
                      size={16}
                      strokeWidth={2}
                      className="-translate-y-0.5 text-black"
                    />
                    استلام
                  </span>
                </button>
                {deliveryAvailable && (
                  <button
                    type="button"
                    onClick={() => selectOrderType("DELIVERY")}
                    className={[
                      "flex min-h-[35px] flex-1 items-center justify-center rounded-sm px-4 py-1 text-black",
                      orderType === "DELIVERY"
                        ? "border border-white bg-white shadow-sm"
                        : "border border-transparent",
                    ].join(" ")}
                    data-testid="menu-order-type-delivery"
                    dir="rtl"
                  >
                    <span className="flex items-center gap-1.5 text-sm font-bold">
                      <Bike
                        size={16}
                        strokeWidth={2}
                        className="-translate-y-0.5 text-black"
                      />
                      توصيل
                    </span>
                  </button>
                )}
              </div>
            </div>
            {orderType === "STORE" ? (
              <div
                className="mt-0 grid min-h-16 grid-cols-2 items-center py-0"
                data-testid="pickup-preparation-information"
                dir="ltr"
              >
                <div className="flex h-full min-w-0 items-center justify-center">
                  {branchMapsUrl ? (
                    <button
                      type="button"
                      onClick={() => setDirectionsSheetOpen(true)}
                      className="flex items-center"
                      data-testid="pickup-directions-link"
                      dir="rtl"
                    >
                      <img
                        src={DIRECTIONS_MARK_URL}
                        alt="الاتجاهات"
                        className="h-16 w-16 object-contain"
                        data-testid="pickup-directions-icon"
                      />
                    </button>
                  ) : (
                    <div
                      className="flex flex-col items-center text-gray-400"
                      data-testid="pickup-directions-unavailable"
                      dir="rtl"
                    >
                      <Navigation size={18} strokeWidth={2} />
                      <span className="mt-0.5 text-xs">الاتجاهات</span>
                    </div>
                  )}
                </div>
                <div
                  className="flex h-full min-w-0 flex-col items-center justify-center text-center"
                  dir="rtl"
                >
                  <div className="text-xs text-gray-500">وقت التحضير</div>
                  <div
                    className="text-sm font-extrabold text-black num-badge"
                    data-testid="restaurant-preparation-time"
                  >
                    {preparationTime} دقيقة
                  </div>
                  {restaurant.address && (
                    <div
                      className="mt-0.5 max-w-full truncate text-xs text-gray-500"
                      data-testid="pickup-address"
                    >
                      {restaurant.address}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div
                className="mt-0 grid min-h-16 grid-cols-2 items-center py-0"
                data-testid="delivery-preparation-information"
                dir="ltr"
              >
                <div
                  className="flex h-full flex-col items-center justify-center text-center"
                  dir="rtl"
                >
                  <div>
                    <div className="text-xs text-gray-500">رسوم التوصيل</div>
                    <div
                      className="text-sm font-extrabold text-black num-badge"
                      data-testid="delivery-fee"
                    >
                      <SAR>{restaurant.delivery_fee || 0}</SAR>
                    </div>
                  </div>
                </div>
                <div
                  className="flex h-full flex-col items-center justify-center text-center"
                  dir="rtl"
                >
                  <div className="text-xs text-gray-500">مدة التوصيل</div>
                  <div
                    className="text-sm font-extrabold text-black num-badge"
                    data-testid="restaurant-preparation-time"
                  >
                    {preparationTime} دقيقة
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {!av.accepting_orders && (
        <div className="max-w-md mx-auto px-5 mt-4">
          <ClosedBanner availability={av} />
        </div>
      )}

      {activeOrder && (
        <div className="max-w-md mx-auto px-5 mt-4">
          <Link
            to={`/order/${activeOrder.id}`}
            data-testid="resume-active-order"
            className={
              "flex items-center gap-2 rounded-2xl border border-green-200 bg-green-50 px-4 "
              + "py-3 text-green-800"
            }
          >
            <span className="w-8 h-8 rounded-full bg-green-600 text-white grid place-items-center">
              <Clock size={16}/>
            </span>
            <div className="flex-1">
              <div className="font-extrabold text-sm">متابعة الطلب</div>
              <div className="text-xs text-green-700">
                طلبك رقم <span className="num-badge font-bold">#{activeOrder.order_number}</span>
                {" "}ما زال قيد التنفيذ — اضغط للمتابعة
              </div>
            </div>
            <ArrowLeft size={18} className="rotate-180" />
          </Link>
        </div>
      )}

      <div
        ref={categoryNavRef}
        className={[
          "sticky top-0 z-20 max-w-md mx-auto mt-6 px-5 pt-2 bg-white/95 backdrop-blur-sm",
          isCategoryNavSticky ? "shadow-[0_6px_12px_-10px_rgba(17,24,39,0.28)]" : "",
        ].filter(Boolean).join(" ")}
        data-testid="sticky-category-nav"
      >
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-5 px-5">
          {categories.map((c) => (
            <button
              key={c.id}
              ref={(node) => {
                if (node) categoryButtonRefs.current.set(c.id, node);
                else categoryButtonRefs.current.delete(c.id);
              }}
              onClick={() => scrollToCategory(c.id)}
              data-testid={`cat-${c.id}`}
              className={`pill whitespace-nowrap px-4 py-2 text-sm font-bold border ${
                activeCat === c.id
                  ? "brand-grad text-white border-transparent"
                  : "bg-white text-gray-800 border-orange-100"
              }`}
            >
              {c.name}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-md mx-auto px-5 mt-4 space-y-8">
        {categories.map((category) => {
          const sectionProducts = products.filter((product) => product.category_id === category.id);

          return (
            <section
              key={category.id}
              ref={(node) => {
                if (node) sectionRefs.current.set(category.id, node);
                else sectionRefs.current.delete(category.id);
              }}
              className="scroll-mt-24 space-y-3 border-b border-orange-100/60"
              data-category-id={category.id}
              data-testid={`menu-section-${category.id}`}
            >
              <h2 className="font-extrabold text-2xl" data-testid={`section-title-${category.id}`}>
                {category.name}
              </h2>
              {sectionProducts.map((p, productIndex) => (
                <div
                  key={p.id}
                  className={[
                    "px-3 py-[5px] flex gap-3 flex-row-reverse cursor-pointer",
                    productIndex < sectionProducts.length - 1
                      ? "border-b border-orange-100/60"
                      : "",
                    (p.option_groups || []).length > 0 ? "h-[180px]" : "h-[170px]",
                  ].filter(Boolean).join(" ")}
                  onClick={() => setSelectedProduct(p)}
                  data-testid={`product-${p.id}`}
                >
                  <div
                    className={`shrink-0 ${
                      (p.option_groups || []).length > 0
                        ? "h-[170px] w-[150px]"
                        : "mt-[5px] h-[150px] w-[150px]"
                    }`}
                    dir="ltr"
                  >
                    <div className="relative h-[150px] w-[150px]">
                      <div
                        className={
                          "customer-menu-image-frame w-full h-full rounded-2xl overflow-hidden food-img"
                        }
                      >
                        {p.image_url ? (
                          <img
                            src={absoluteUrl(p.image_url)}
                            alt=""
                            className="w-full h-full object-contain"
                          />
                        ) : (
                          <div className="w-full h-full grid place-items-center text-[55px]">🍽️</div>
                        )}
                      </div>
                      <ProductListAction
                        product={p}
                        cart={cart}
                        onOpenDetails={() => setSelectedProduct(p)}
                      />
                    </div>
                    {(p.option_groups || []).length > 0 && (
                      <div
                        className="mt-1 text-center text-xs font-medium text-gray-500"
                        data-testid={`customize-${p.id}`}
                        dir="rtl"
                      >
                        تخصيص
                      </div>
                    )}
                  </div>
                  <div className="min-h-0 h-full flex-1 overflow-hidden text-right">
                    <div className="flex h-full flex-col justify-between">
                      <div className="space-y-1">
                        <div className="line-clamp-2 font-extrabold text-base">{p.name}</div>
                        <div
                          className="line-clamp-2 text-sm text-gray-600"
                          data-testid={`product-description-${p.id}`}
                        >
                          {p.description?.trim() || p.name}
                        </div>
                      </div>
                      <MenuProductPrice productId={p.id} value={p.price} />
                    </div>
                  </div>
                </div>
              ))}
              {sectionProducts.length === 0 && (
                <div className="text-center text-gray-500 py-6">لا توجد أصناف في هذا القسم</div>
              )}
            </section>
          );
        })}
      </div>

      {cart.count > 0 && (
        <div className="fixed bottom-0 inset-x-0 z-30">
          <div className="max-w-md mx-auto p-4">
            <Link
              to={`${publicBranchBase}/cart`}
              data-testid="view-cart"
              className={
                "flex items-center gap-2 pill brand-grad text-white px-5 py-3.5 "
                + "tap-target card-shadow"
              }
            >
              <span className="inline-flex shrink-0 self-center items-center gap-2">
                <span
                  className={
                    "grid h-6 w-6 place-items-center rounded-full border border-white/70 "
                    + "bg-[#EA580C] text-xs font-extrabold leading-none num-badge"
                  }
                  data-testid="cart-bar-count"
                >
                  <span className="cart-bar-count-value">{cart.count}</span>
                </span>
                <span
                  className="inline-flex h-6 self-center items-center font-extrabold leading-none"
                  data-testid="cart-bar-total"
                >
                  <SAR className="cart-bar-price-value">{cart.total}</SAR>
                </span>
              </span>
              <span
                className={
                  "flex h-6 flex-1 self-center items-center justify-end text-left font-bold "
                  + "leading-none"
                }
                data-testid="cart-bar-label"
              >
                <span className="cart-bar-label-value">عرض السلة</span>
              </span>
              <span
                className={
                  "grid h-6 w-6 shrink-0 self-center place-items-center rounded-full bg-white "
                  + "text-[#F97316]"
                }
                data-testid="cart-bar-arrow"
              >
                <ChevronLeft size={14} />
              </span>
            </Link>
          </div>
        </div>
      )}

      {selectedProduct && (
        <ProductSheet product={selectedProduct} onClose={() => setSelectedProduct(null)}
          onAdd={(line) => { cart.addCustomized(line); setSelectedProduct(null); }} />
      )}
      {branchMapsQuery && (
        <DirectionsAppSheet
          destination={branchMapsQuery}
          open={directionsSheetOpen}
          onOpenChange={setDirectionsSheetOpen}
        />
      )}
    </div>
  );
}

function MenuProductPrice({ productId, value }) {
  return (
    <div
      className="mt-0.5 text-xl font-bold text-black"
      data-testid={`product-price-${productId}`}
    >
      <SAR>{value}</SAR>
    </div>
  );
}

function ProductListAction({ product, cart, onOpenDetails }) {
  const hasOptionGroups = (product.option_groups || []).length > 0;
  const cartLine = cart.items.find((item) => (
    item.product_id === product.id && (item.selected_addons || []).length === 0
  ));
  const quantity = cartLine?.quantity || 0;

  const handleIncrease = (event) => {
    event.stopPropagation();
    if (hasOptionGroups) {
      onOpenDetails();
      return;
    }
    cart.addItem(product);
  };

  const handleDecrease = (event) => {
    event.stopPropagation();
    if (cartLine) cart.setQty(cartLine._key, cartLine.quantity - 1);
  };

  return (
    <ProductQuantityControl
      quantity={hasOptionGroups ? 0 : quantity}
      onDecrease={handleDecrease}
      onIncrease={handleIncrease}
      placement="floating"
      containerTestId={`quantity-controls-${product.id}`}
      decreaseTestId={`decrease-${product.id}`}
      increaseTestId={`add-${product.id}`}
      quantityTestId={`quantity-${product.id}`}
      deleteIconTestId={`delete-icon-${product.id}`}
      minusIconTestId={`minus-icon-${product.id}`}
      increaseLabel={`إضافة ${product.name}`}
      decreaseLabel={`تقليل ${product.name}`}
      plusStrokeWidth={2.5}
    />
  );
}
